import { Pipe, PipeTransform } from "@angular/core";

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/**
 * Formats an "updated" timestamp using the product's relative-time buckets:
 *
 *   0–59 seconds  → "just now"
 *   1–59 minutes  → "N min ago"
 *   1–23 hours    → "N hr ago"
 *   24–47 hours   → "yesterday"
 *   2–6 days      → "N days ago"
 *   7+ days       → an absolute date ("Jun 5"), including the year when the
 *                   date falls in a previous year ("Jun 5, 2025")
 */
@Pipe({ name: "relativeTime", standalone: true })
export class RelativeTimePipe implements PipeTransform {
    transform(value: string | Date | null | undefined): string {
        if (value === null || value === undefined) return "";

        const date = new Date(value);
        const time = date.getTime();
        if (Number.isNaN(time)) return "";

        const now = Date.now();
        const seconds = Math.floor((now - time) / 1000);

        if (seconds < MINUTE) return "just now";
        if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)} min ago`;
        if (seconds < DAY) return `${Math.floor(seconds / HOUR)} hr ago`;
        if (seconds < 2 * DAY) return "yesterday";
        if (seconds < 7 * DAY) return `${Math.floor(seconds / DAY)} days ago`;

        return this.formatAbsolute(date, new Date(now));
    }

    private formatAbsolute(date: Date, now: Date): string {
        const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
        if (date.getFullYear() === now.getFullYear()) return monthDay;
        return `${monthDay}, ${date.getFullYear()}`;
    }
}
